import { useMutation } from '@apollo/client';
import React, { FC, useMemo } from 'react';

import AIGenerateIcon from 'components/common/icons/AIGenerateIcon';
import DotDropdown from 'components/common/navigation/DotDropdown';
import RefreshIcon from 'components/common/icons/RefreshIcon';
import TrashIcon from 'components/common/icons/TrashIcon';
import { FeedbackAISummary } from 'types/questions.types';
import dayjs from 'utils/extendedDayJS';
import { AI_FEEDBACK_DATE_TIME_FORMAT } from 'constants/dates';
import {
  DELETE_FEEDBACK_SUMMARY_MUTATION,
  DELETE_FEEDBACK_SUMMARY_RESULT,
  DELETE_FEEDBACK_SUMMARY_VAR
} from 'graphql/mutations/opeanai/openai.mutation';

interface AIFeedbackSummaryProps {
  feedbackSummary: FeedbackAISummary;
  setOpenGenerateFeedbackModal: () => void;
  refetchList: () => void;
}

/**
 * Section on insights page's feedback section to display AI summary result for long type feedback questions.
 * @param param0
 * @returns
 */
export const AIFeedbackSummary: FC<React.PropsWithChildren<AIFeedbackSummaryProps>> = ({
  feedbackSummary,
  setOpenGenerateFeedbackModal,
  refetchList
}) => {
  const { summary: aiSummary, updatedAt } = feedbackSummary;
  const feedbackSummaryParsed = useMemo(() => {
    return aiSummary.split('<br><br>');
  }, [aiSummary]);

  const [deleteFeedbackSummary] = useMutation<
    DELETE_FEEDBACK_SUMMARY_RESULT,
    DELETE_FEEDBACK_SUMMARY_VAR
  >(DELETE_FEEDBACK_SUMMARY_MUTATION);

  /**
   * Handle delete feedback summary
   */
  const handleDelete = async (): Promise<void> => {
    const result = await deleteFeedbackSummary({
      variables: {
        feedbackSummaryId: feedbackSummary.id
      }
    });

    if (result.data?.deleteFeedbackSummary.ok) {
      // Refetch question response data.
      refetchList();
    }
  };

  return (
    <div className="tw-mb-5 tw-mt-3 tw-h-full tw-min-h-[120px] tw-items-center tw-justify-center tw-rounded-lg tw-border-[1.5px] tw-border-solid tw-border-border-grey tw-p-4">
      <div className="tw-flex tw-items-center tw-justify-between">
        <div className="tw-flex">
          <AIGenerateIcon />
          <div className="tw-ml-1 tw-text-sm tw-font-medium tw-text-dark-black">
            Summarized with AI
          </div>
        </div>

        <DotDropdown
          dropdownItems={[
            {
              id: 'TRY_AGAIN',
              label: 'Try again',
              svgIcon: <RefreshIcon />,
              onClick: (): void => {
                setOpenGenerateFeedbackModal();
              }
            },
            {
              id: 'DELETE',
              label: 'Delete',
              svgIcon: <TrashIcon />,
              onClick: (): void => {
                handleDelete();
              }
            }
          ]}
        />
      </div>
      <div className="tw-mt-4">
        {feedbackSummaryParsed.map((summary, idx) => {
          return (
            <div
              dangerouslySetInnerHTML={{ __html: summary }}
              className="tw-mb-3"
              key={`summary-${idx}`}
            />
          );
        })}
      </div>

      <div className="tw-mt-4 tw-text-xs tw-text-primary-grey">
        Generated on {dayjs(updatedAt).format(AI_FEEDBACK_DATE_TIME_FORMAT)}
      </div>
    </div>
  );
};
